import { useNavigate, useOutletContext, useSearchParams } from "react-router";
import { useEffect, useState, type ReactNode } from "react";
import { formatDate } from "@/app/(app)/_lib/format";
import type { AppOutletContext } from "@/src/layouts/AppLayout";
import { Button } from "@/components/button";
import { Icon } from "@/components/icons";
import { AnnouncementBanners } from "@/components/announcement-banner";
import { ActiveMonitoringAlertsSection } from "./active-monitoring-alerts-card";
import { RecentScreeningsSection } from "./recent-screenings-card";
import { WelcomePopup } from "./welcome-popup";
import {
  StatCardSkeleton,
  ChartCardSkeleton,
  DashboardSkeleton,
  gridColsForCount,
} from "./skeletons";
import { ChartCard } from "./chart-card";
import {
  SCREENING_REQUESTS_SERIES,
  MATCHED_NAMES_SERIES,
  NEW_MATCHES_SERIES,
  buildScreeningSeries,
  isScreeningSeriesEmpty,
  buildMonitoringSeries,
  isMonitoringTrendEmpty,
} from "./chart-series";
import {
  OpenAlertsCard,
  MonitoredNamesCard,
  ScreeningRequests30dCard,
  ScreeningRequests12mCard,
  SCREENING_REQUESTS_INFO,
} from "./stat-cards";
import { TabTransition } from "@/components/tab-transition";
import { resolvePlan } from "../_plan";
import { optOutOnboarding } from "@/src/services/account_service";
import { lastAlertFromTrend, type LastAlert } from "@/src/services/monitoring_service";
import {
  useScreeningCounts,
  useMonitoringPreview,
  useMonitoringTrend,
  type ScreeningWidgetData,
  type MonitoringWidgetData,
  type MonitoringTrendData,
} from "./dashboard-data-hooks";

export function DashboardClient() {
  const [params] = useSearchParams();
  const ctx = useOutletContext<AppOutletContext | null>();
  const infoResolved = ctx?.infoResolved ?? false;
  const enableMonitoring = ctx?.enableMonitoring ?? true;
  const hasActivePlan = ctx?.hasActivePlan ?? true;
  const mainInsetLeft = ctx?.mainInsetLeft ?? 0;
  const previewMode = Boolean(params.get("state")) || Boolean(params.get("tools"));
  const tools = resolvePlan(params.get("tools"), enableMonitoring).tools;
  const monitoring = tools.includes("monitoring");
  const screening = tools.includes("screening");
  const empty = params.get("state") === "empty";
  const loading = params.get("state") === "loading";
  const welcomeParam = params.get("welcome");

  const liveScreening = screening && !previewMode;
  const screeningData = useScreeningCounts(liveScreening, infoResolved);

  const liveMonitoring = monitoring && !previewMode;
  const monitoringData = useMonitoringPreview(liveMonitoring, infoResolved);
  const monitoringTrendData = useMonitoringTrend(liveMonitoring, infoResolved);

  const optOut = ctx?.apiUser?.optOut ?? true;
  const welcomeRequested =
    welcomeParam === "0" ? false : welcomeParam === "1" || (infoResolved && !optOut);
  const welcomeVariant: "general" | "screening" | "monitoring" =
    screening && monitoring ? "general" : screening ? "screening" : "monitoring";

  const welcomeKey = `${welcomeVariant}:${welcomeParam ?? ""}`;
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  useEffect(() => {
    setDismissedKey(null);
  }, [welcomeKey]);
  const welcomeOpen = hasActivePlan && welcomeRequested && dismissedKey !== welcomeKey;

  const monitoringOnly = monitoring && !screening;

  const entranceKey = `${tools.join("-")}:${empty ? "empty" : loading ? "loading" : "full"}`;

  return (
    <div className="flex min-h-full flex-col gap-2 bg-grey-25 px-4 py-4 sm:px-8">
      <AnnouncementBanners />
      <WelcomePopup
        open={welcomeOpen}
        variant={welcomeVariant}
        onClose={() => setDismissedKey(welcomeKey)}
        onSkipForever={() => {
          setDismissedKey(welcomeKey);

          ctx?.markOnboardingOptedOut();
          void optOutOnboarding();
        }}
        insetLeft={mainInsetLeft}
      />
      {/* One subtle entrance (fade + 8px rise) on load and on plan/state
          change; reduced-motion aware via TabTransition. */}
      <TabTransition transitionKey={entranceKey} className="flex flex-col gap-2">
        <Hero screening={screening} />
        {loading ? (
          <DashboardSkeleton monitoring={monitoring} screening={screening} />
        ) : (
          <>
            <StatsRow
              monitoring={monitoring}
              screening={screening}
              empty={empty}
              screeningData={screeningData}
              monitoringData={monitoringData}
              monitoringTrend={monitoringTrendData}
            />
            {monitoringOnly ? (
              <section className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                <div className="flex flex-col gap-2">
                  {monitoringTrendData?.loading ? (
                    <ChartCardSkeleton />
                  ) : (
                    <ChartCard
                      title="Matched names"
                      subtitle="Monitored names with at least one active match"
                      series={
                        monitoringTrendData?.trend
                          ? buildMonitoringSeries(monitoringTrendData.trend)
                          : MATCHED_NAMES_SERIES
                      }
                      empty={
                        empty ||
                        (monitoringTrendData?.trend
                          ? isMonitoringTrendEmpty(monitoringTrendData.trend)
                          : false)
                      }
                      emptyTitle="No matched names yet"
                      emptyHint="Add names to monitor to start tracking matches."
                    />
                  )}
                  <ChartCard
                    title="New matches"
                    subtitle="Fresh matches on monitored names"
                    series={NEW_MATCHES_SERIES}
                    empty={empty || liveMonitoring}
                    emptyTitle={
                      liveMonitoring && !empty ? "Match trend coming soon" : "No new matches yet"
                    }
                    emptyHint={
                      liveMonitoring && !empty
                        ? "Historical match trends will appear here once available."
                        : "New matches will appear here once monitoring is active."
                    }
                  />
                </div>
                <ActiveMonitoringAlertsSection
                  monitoring={monitoring}
                  previewMode={previewMode}
                  empty={empty}
                  skeletonRows={6}
                />
              </section>
            ) : (
              <>
                <ChartsRow
                  monitoring={monitoring}
                  screening={screening}
                  empty={empty}
                  screeningData={screeningData}
                  monitoringTrend={monitoringTrendData}
                />
                <BottomRow
                  monitoring={monitoring}
                  screening={screening}
                  empty={empty}
                  previewMode={previewMode}
                />
              </>
            )}
          </>
        )}
      </TabTransition>
    </div>
  );
}

function Hero({ screening }: { screening: boolean }) {
  const navigate = useNavigate();
  const ctx = useOutletContext<AppOutletContext | null>();
  const firstName = ctx?.apiUser?.name?.trim().split(/\s+/)[0] ?? "";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const todayLabel = formatDate(new Date());

  return (
    <section className="card flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="flex min-w-0 flex-col gap-1">
        <h2 className="text-h2 text-black">
          {greeting}
          {firstName ? `, ${firstName}` : ""}
        </h2>
        <span className="text-small text-grey-600">Compliance overview · {todayLabel}</span>
      </div>
      <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
        {screening ? (
          <Button
            hierarchy="primary"
            size="md"
            leftIcon={<Icon name="search" size={20} />}
            onClick={() => navigate("/screening-portal")}
            className="w-full shrink-0 sm:w-auto"
          >
            Run screening
          </Button>
        ) : (
          <Button
            hierarchy="primary"
            size="md"
            leftIcon={<Icon name="add" size={20} />}
            onClick={() => navigate("/monitoring-portal")}
            className="w-full shrink-0 sm:w-auto"
          >
            Add a name to monitor
          </Button>
        )}
      </div>
    </section>
  );
}

function StatsRow({
  monitoring,
  screening,
  empty,
  screeningData,
  monitoringData,
  monitoringTrend,
}: {
  monitoring: boolean;
  screening: boolean;
  empty: boolean;
  screeningData: ScreeningWidgetData | null;
  monitoringData: MonitoringWidgetData | null;
  /** "Last alert" fallback while monitoring/statistics/ lacks last_alert_at. */
  monitoringTrend: MonitoringTrendData | null;
}) {
  const screeningLoading = screeningData?.loading ?? false;
  const liveCounts = screeningData?.counts ?? null;
  const screeningCards: ReactNode[] = [
    screening &&
      (screeningLoading ? (
        <StatCardSkeleton key="screening-30d" />
      ) : (
        <ScreeningRequests30dCard key="screening-30d" empty={empty} counts={liveCounts} />
      )),
    screening &&
      (screeningLoading ? (
        <StatCardSkeleton key="screening-12m" />
      ) : (
        <ScreeningRequests12mCard key="screening-12m" empty={empty} counts={liveCounts} />
      )),
  ];
  const monitoringLoading = monitoringData?.loading ?? false;
  const liveKpi = monitoringData?.kpi ?? null;
  const liveOpenAlertsCount = monitoringData ? monitoringData.openAlertsCount : null;
  const lastAlert: LastAlert | null = liveKpi?.lastAlertAt
    ? { period: "exact", at: liveKpi.lastAlertAt }
    : monitoringTrend?.trend
      ? lastAlertFromTrend(monitoringTrend.trend)
      : null;
  const monitoringCards: ReactNode[] = [
    monitoring &&
      (monitoringLoading || monitoringTrend?.loading ? (
        <StatCardSkeleton key="open-alerts" />
      ) : (
        <OpenAlertsCard
          key="open-alerts"
          empty={empty}
          count={liveOpenAlertsCount}
          lastAlert={lastAlert}
        />
      )),
    monitoring &&
      (monitoringLoading ? (
        <StatCardSkeleton key="monitored-names" />
      ) : (
        <MonitoredNamesCard key="monitored-names" empty={empty} kpi={liveKpi} />
      )),
  ];
  const cards: ReactNode[] = (
    empty ? [...monitoringCards, ...screeningCards] : [...screeningCards, ...monitoringCards]
  ).filter(Boolean);

  if (cards.length === 0) return null;
  return (
    <div className="@container">
      <section className={`grid grid-cols-1 gap-2 ${gridColsForCount(cards.length)}`}>
        {cards}
      </section>
    </div>
  );
}

function ChartsRow({
  monitoring,
  screening,
  empty,
  screeningData,
  monitoringTrend,
}: {
  monitoring: boolean;
  screening: boolean;
  empty: boolean;
  screeningData: ScreeningWidgetData | null;
  /** Live monitoring match trend (null on the mock/preview path → mock series). */
  monitoringTrend: MonitoringTrendData | null;
}) {
  const screeningLoading = screeningData?.loading ?? false;
  const liveCounts = screeningData?.counts ?? null;
  const monitoringTrendLoading = monitoringTrend?.loading ?? false;
  const liveTrend = monitoringTrend?.trend ?? null;
  const charts: ReactNode[] = [
    screening &&
      (screeningLoading ? (
        <ChartCardSkeleton key="screening" />
      ) : (
        <ChartCard
          key="screening"
          title="Screening requests"
          titleInfo={SCREENING_REQUESTS_INFO}
          subtitle="Consumption of screening requests"
          series={liveCounts ? buildScreeningSeries(liveCounts) : SCREENING_REQUESTS_SERIES}
          empty={empty || (liveCounts ? isScreeningSeriesEmpty(liveCounts) : false)}
          emptyTitle="No screening runs yet"
          emptyHint="Run a screening to start populating this trend."
        />
      )),
    monitoring &&
      (monitoringTrendLoading ? (
        <ChartCardSkeleton key="matched-names" />
      ) : (
        <ChartCard
          key="matched-names"
          title="Matched names"
          subtitle="Monitored names with at least one active match"
          series={liveTrend ? buildMonitoringSeries(liveTrend) : MATCHED_NAMES_SERIES}
          empty={empty || (liveTrend ? isMonitoringTrendEmpty(liveTrend) : false)}
          emptyTitle="No matched names yet"
          emptyHint="Add names to monitor to start tracking matches."
        />
      )),
  ].filter(Boolean);

  if (charts.length === 0) return null;
  const cols = charts.length >= 2 ? "lg:grid-cols-2" : "";
  return <section className={`grid grid-cols-1 gap-2 ${cols}`}>{charts}</section>;
}

function BottomRow({
  monitoring,
  screening,
  empty,
  previewMode,
}: {
  monitoring: boolean;
  screening: boolean;
  empty: boolean;
  previewMode: boolean;
}) {
  const recentCard = (showMinScore: boolean) => (
    <RecentScreeningsSection
      screening={screening}
      previewMode={previewMode}
      empty={empty}
      showMinScore={showMinScore}
    />
  );
  const alertsCard = () => (
    <ActiveMonitoringAlertsSection
      monitoring={monitoring}
      previewMode={previewMode}
      empty={empty}
    />
  );

  if (screening && monitoring) {
    return (
      <section className="grid grid-cols-1 gap-2 lg:grid-cols-2">
        {recentCard(false)}
        {alertsCard()}
      </section>
    );
  }
  if (screening) {
    return recentCard(true);
  }
  if (monitoring) {
    return alertsCard();
  }
  return null;
}
